/**
 * Chat Store - Pinia store for managing chat data and Firebase integration
 * Handles real-time chat updates, message sending, and chat management
 * Updated for multi-tenant inbox system
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  limit,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  db,
  functions,
  COLLECTIONS,
  FIELDS,
  CHAT_STATUS,
  MESSAGE_TYPE,
  SENDER,
} from "../services/firebase";
import { useUserStore } from "./user";

const updateConversationStatusCallable = httpsCallable(
  functions,
  "updateConversationStatusV2"
);
const sendWebsiteMessageCallable = httpsCallable(
  functions,
  "sendWebsiteMessageV2"
);
const sendLineMessageCallable = httpsCallable(functions, "sendLineMessageV2");
const sendFacebookMessageCallable = httpsCallable(
  functions,
  "sendFacebookMessageV2"
);
const sendInstagramMessageCallable = httpsCallable(
  functions,
  "sendInstagramMessageV2"
);

export const useChatStore = defineStore("chat", () => {
  // State
  const chats = ref([]);
  const selectedChatId = ref(null);
  const messages = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const unreadChats = ref(new Set());
  
  // Client-side throttling: track last send time
  const lastSendTime = ref(0);
  const MIN_SEND_INTERVAL = 1000; // 1 second minimum between sends

  // Getters
  const selectedChat = computed(() => {
    return chats.value.find((chat) => chat.id === selectedChatId.value);
  });

  const unreadCount = computed(() => {
    return chats.value.reduce(
      (total, chat) => total + (chat.unreadCount || 0),
      0
    );
  });

  const activeChats = computed(() => {
    return chats.value.filter((chat) => chat.status === CHAT_STATUS.ACTIVE);
  });

  const pendingChats = computed(() => {
    return chats.value.filter((chat) => chat.status === CHAT_STATUS.PENDING);
  });

  const handoffChats = computed(() => {
    return chats.value.filter(
      (chat) => chat.status === CHAT_STATUS.HUMAN_HANDOFF
    );
  });

  // Actions
  const loadChats = async () => {
    try {
      loading.value = true;
      error.value = null;

      // Check if user is authenticated first
      const userStore = useUserStore();
      if (!userStore.isAuthenticated || !userStore.currentOrgId) {
        console.log("User not authenticated or no org selected, skipping chats load");
        chats.value = [];
        return;
      }

      // Query conversations collection instead of inbox
      const conversationsQuery = query(
        collection(db, `orgs/${userStore.currentOrgId}/conversations`),
        orderBy("lastAt", "desc")
      );

      onSnapshot(conversationsQuery, (snapshot) => {
        chats.value = snapshot.docs.map((doc) => {
          const data = doc.data();
          const participants = data.participants || {};
          
          // Helper function for timestamp conversion
          const toDate = (timestamp) => {
            return timestamp?.toDate ? timestamp.toDate() : (timestamp || new Date());
          };
          
          return {
            id: doc.id,
            threadKey: data.threadKey,
            provider: data.provider ?? participants.channel ?? data.channel ?? "line",
            channel: data.channel ?? participants.channel ?? data.provider ?? "line",
            
            // Customer identification
            customerId: participants.customerId ?? data.customerId ?? data.threadKey,
            
            // 👉 Map ชื่อ + รูปมาเก็บในฟิลด์ที่ UI ใช้ (fallback หลายชั้น)
            customerName: participants.displayName ?? data.customerName ?? data.participants?.displayName ?? `${data.provider || "Unknown"} User`,
            customerAvatar: participants.pictureUrl ?? data.customerAvatar ?? data.participants?.pictureUrl ?? null,
            
            // Message info
            lastMessage: data.lastMessage ?? "",
            lastMessageTime: toDate(data.lastAt ?? data.updatedAt),
            lastMessageSender: "customer",
            
            // Status tracking
            status: data.status ?? "open",
            unreadCount: data.unread ?? 0,
            
            // Metadata
            organizationId: userStore.currentOrgId,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
            
            // Keep participants object for additional fallback
            participants: participants,
          };
        });
        
        console.log(`Loaded ${chats.value.length} conversations`);
      }, (err) => {
        error.value = err.message;
        console.error("Error loading conversations:", err);
      });
    } catch (err) {
      error.value = err.message;
      console.error("Error setting up conversations listener:", err);
    } finally {
      loading.value = false;
    }
  };

  const selectChat = async (chatId) => {
    if (selectedChatId.value === chatId) return;

    selectedChatId.value = chatId;
    await loadMessages(chatId);

    // Mark messages as read
    if (selectedChat.value) {
      await markAsRead(chatId);
    }
  };

  const loadMessages = async (chatId) => {
    try {
      loading.value = true;
      error.value = null;

      // Clear previous messages
      messages.value = [];

      const userStore = useUserStore();
      if (!userStore.hasOrganization) {
        return;
      }

      const chat = chats.value.find((item) => item.id === chatId);
      const threadKey = chat?.threadKey ?? chatId;

      // Real-time listener for messages in selected chat (threadKey)
      const messagesQuery = query(
        collection(db, `orgs/${userStore.currentOrgId}/inbox`),
        where("threadKey", "==", threadKey),
        orderBy("ts", "asc")
      );

      onSnapshot(
        messagesQuery,
        (snapshot) => {
          const toDate = (value) => {
            if (!value) return new Date();
            if (typeof value.toDate === "function") {
              return value.toDate();
            }
            if (typeof value === "number") {
              return new Date(value);
            }
            return new Date(value);
          };

          messages.value = snapshot.docs.map((doc) => {
            const data = doc.data();

            return {
              id: doc.id,
              ...data,
              timestamp: toDate(data.ts ?? data.createdAt),
              content: data.text || `[${data.provider}]`,
              sender: data.direction === "in" ? "customer" : "agent",
              type: data.text ? "text" : "attachment",
              // Include userProfile data for display
              userProfile: data.userProfile || null,
            };
          });
        },
        (err) => {
          error.value = err.message;
          console.error("Error in messages listener:", err);
        }
      );
    } catch (err) {
      error.value = err.message;
      console.error("Error loading messages:", err);
    } finally {
      loading.value = false;
    }
  };

  const sendMessage = async (content, type = MESSAGE_TYPE.TEXT) => {
    if (!selectedChatId.value || !content.trim()) return;

    // Client-side throttling: prevent rapid successive sends
    const now = Date.now();
    if (now - lastSendTime.value < MIN_SEND_INTERVAL) {
      error.value = "Please wait before sending another message";
      return;
    }

    try {
      loading.value = true;
      error.value = null;
      lastSendTime.value = now;

      const userStore = useUserStore();
      if (!userStore.hasOrganization) {
        throw new Error("No organization selected");
      }

      // Get the selected chat to determine the channel and user ID
      const selectedChat = chats.value.find(
        (chat) => chat.id === selectedChatId.value
      );
      if (!selectedChat) {
        throw new Error("Selected chat not found");
      }

      // Send message to the actual messaging platform (Cloud Function handles inbox creation)
      const trimmedContent = content.trim();
      if (selectedChat.provider === "line") {
        await sendLineMessageCallable({
          threadKey: selectedChat.threadKey,
          message: trimmedContent,
        });
      } else if (selectedChat.provider === "facebook") {
        await sendFacebookMessageCallable({
          threadKey: selectedChat.threadKey,
          message: trimmedContent,
        });
      } else if (selectedChat.provider === "instagram") {
        await sendInstagramMessageCallable({
          threadKey: selectedChat.threadKey,
          message: trimmedContent,
        });
      } else if (selectedChat.provider === "website") {
        const messagePayload = {
          type,
          text: trimmedContent,
        };

        await sendWebsiteMessageCallable({
          sessionId: selectedChat.threadKey,
          message: messagePayload,
        });
      }
    } catch (err) {
      // Handle rate limit errors specifically
      if (err.code === "resource-exhausted" || err.message?.includes("Rate limit")) {
        const retryAfter = err.details?.retryAfter || 60;
        error.value = err.message || `Rate limit exceeded. Please try again in ${retryAfter} second(s).`;
      } else {
        error.value = err.message;
      }
      console.error("Error sending message:", err);
    } finally {
      loading.value = false;
    }
  };

  const markAsRead = async (chatId) => {
    try {
      const userStore = useUserStore();
      if (!userStore.currentOrgId) return;

      // อัปเดตสถานะอ่านใน conversations (ไม่แตะ inbox)
      const conversationRef = doc(
        db,
        `orgs/${userStore.currentOrgId}/conversations/${chatId}`
      );
      
      await updateDoc(conversationRef, {
        agentLastReadAt: serverTimestamp(),
        unread: 0,
        updatedAt: serverTimestamp()
      });

      console.log(`Marked ${chatId} as read`);
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const humanHandoff = async () => {
    if (!selectedChatId.value) return;

    try {
      loading.value = true;
      error.value = null;

      // Send system message indicating human handoff
      await sendMessage(
        "This conversation has been transferred to a human agent.",
        MESSAGE_TYPE.SYSTEM
      );

      // For inbox system, we don't have chat status updates yet
      // This is a placeholder for future implementation
      console.log("Chat handoff completed - status tracking not implemented for inbox system yet");
    } catch (err) {
      error.value = err.message;
      console.error("Error in human handoff:", err);
    } finally {
      loading.value = false;
    }
  };

  const closeChat = async () => {
    if (!selectedChat.value) return;

    const chat = selectedChat.value;
    const orgId = chat.organizationId;
    const chatId = chat.id;

    if (!orgId || !chatId) {
      error.value = "Cannot close chat: Missing IDs";
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      await sendMessage(
        "Chat has been closed by an agent.",
        MESSAGE_TYPE.SYSTEM
      );

      loading.value = true;

      await updateConversationStatusCallable({
        orgId,
        conversationId: chatId,
        status: CHAT_STATUS.CLOSED,
      });
    } catch (err) {
      error.value = "Failed to close chat.";
      console.error("Error closing chat:", err);
    } finally {
      loading.value = false;
    }
  };

  const updateChatStatus = async (chatId, status) => {
    // For now, this is a placeholder since we don't have chat status in inbox
    // In the future, we could add status tracking to inbox messages
    console.log(`Chat status update not implemented for inbox system: ${chatId} -> ${status}`);
  };

  const clearError = () => {
    error.value = null;
  };

  const clearSelectedChat = () => {
    selectedChatId.value = null;
    messages.value = [];
  };

  return {
    // State
    chats,
    selectedChatId,
    messages,
    loading,
    error,
    unreadChats,

    // Getters
    selectedChat,
    unreadCount,
    activeChats,
    pendingChats,
    handoffChats,

    // Actions
    loadChats,
    selectChat,
    loadMessages,
    sendMessage,
    markAsRead,
    humanHandoff,
    updateChatStatus,
    closeChat,
    clearError,
    clearSelectedChat,
  };
});
